import React, { useContext, useEffect, useState, useMemo } from 'react';
import { FaDatabase, FaChevronDown } from 'react-icons/fa';
import { DataContext } from "../context/DataContext";
import ArtistDetailsPopup from './ArtistDetailsPopup'; // Import the new popup component
import {Button} from "./custom_ui/button.tsx";

const PlatformWiseArtistsData = ({ 
    searchTerm: controlledSearchTerm, 
    setSearchTerm: setControlledSearchTerm,
    searchFilter: controlledSearchFilter,
    setSearchFilter: setControlledSearchFilter
}) => {
    const { applicationData } = useContext(DataContext);
    
    // Internal state for when component is uncontrolled
    const [internalSearchTerm, setInternalSearchTerm] = useState("");
    const [internalSearchFilter, setInternalSearchFilter] = useState("artist_id");

    // Use controlled state if provided, otherwise internal
    const searchTerm = controlledSearchTerm !== undefined ? controlledSearchTerm : internalSearchTerm;
    const setSearchTerm = setControlledSearchTerm || setInternalSearchTerm;
    const searchFilter = controlledSearchFilter !== undefined ? controlledSearchFilter : internalSearchFilter;
    const setSearchFilter = setControlledSearchFilter || setInternalSearchFilter;

    const [currentPage, setCurrentPage] = useState(1);
    const [fullyFilteredArtists, setFullyFilteredArtists] = useState([]);
    const [filteredArtists, setFilteredArtists] = useState([]);
    const itemsPerPage = 10;
    const [selectedArtist, setSelectedArtist] = useState(null); // State for the selected artist
    const [isPopupOpen, setIsPopupOpen] = useState(false); // State for popup visibility

    const filteredData = useMemo(() => {
        if (!applicationData || !applicationData.artists_data) {
            return [];
        }
        return applicationData.artists_data.filter(artist => {
            if (!searchTerm) {
                return true;
            }
            const term = searchTerm.toLowerCase();
            switch (searchFilter) {
                case "artist_id":
                    return artist.artist_id.toLowerCase().includes(term);
                case "label":
                    return artist.labels.some(label => label.toLowerCase().includes(term));
                case "artist_names":
                    return artist.artist_names.some(name => name.toLowerCase().includes(term));
                case "song_names":
                    return artist.song_names.some(name => name.toLowerCase().includes(term));
                default:
                    return true;
            }
        });
    }, [searchTerm, searchFilter, applicationData]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, searchFilter]);

    useEffect(() => {
        if (applicationData && applicationData.artists_data) {
            setFullyFilteredArtists(filteredData);
            setCurrentPage(1);
        }
    }, [searchTerm, searchFilter, applicationData, filteredData]);

    useEffect(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        setFilteredArtists(fullyFilteredArtists.slice(startIndex, endIndex));
    }, [currentPage, fullyFilteredArtists]);

    const handleViewDetails = (artist) => {
        setSelectedArtist(artist);
        setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
        setIsPopupOpen(false);
        setSelectedArtist(null);
    };

    return (
        <div className="bg-gradient-card border border-border/50 rounded-md p-4 mt-8">
            {isPopupOpen && <ArtistDetailsPopup artist={selectedArtist} onClose={handleClosePopup} />}
            <h3 className="text-lg font-semibold mb-4">
                <FaDatabase className="inline-block mr-2 text-green-500" /> Platform Wise Artists Data
            </h3>
            <div className="flex justify-between mb-4">
                <div className="flex items-center">
                    <div className="relative">
                        <select
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className="bg-primary text-secondary rounded-md p-2 pr-8 appearance-none cursor-pointer font-medium"
                        >
                            <option value="artist_id">Artist ID</option>
                            <option value="label">Label</option>
                            <option value="artist_names">Artist Name</option>
                            <option value="song_names">Song Name</option>
                        </select>
                        <FaChevronDown className="absolute right-3 top-2/3 -translate-y-1/2 pointer-events-none text-secondary text-xs" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-gray-700 text-white rounded-md p-2 ml-2"
                    />
                </div>

                <div className="flex items-center">
                    <p className='pe-2'>Total Pages: {Math.ceil(fullyFilteredArtists.length / itemsPerPage)} </p>
                    <Button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="bg-gray-700 text-white rounded-md p-2 mr-2"
                    >
                        Previous
                    </Button>
                    <span>Page {currentPage}</span>
                    <Button
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={filteredArtists.length < itemsPerPage || currentPage * itemsPerPage >= fullyFilteredArtists.length}
                        className="bg-gray-700 text-white rounded-md p-2 ml-2"
                    >
                        Next
                    </Button>
                </div>
            </div>

            <div className='w-full'>
                <div className='w-full overflow-x-auto'>
                    <table className='w-full select-none scroll-smooth'>
                        <thead className='bg-gradient-card/10 border border-border'>
                            <tr>
                                <th className='p-4 text-left'>S.No</th>
                                <th className='p-4 text-left'>Artist ID</th>
                                <th className='p-4 text-left'>Label</th>
                                <th className='p-4 text-left'>Total Artists</th>
                                <th className='p-4 text-left'>Total Songs</th>
                                <th className='p-4 text-left'>Actions</th>
                            </tr>
                        </thead>
                        <tbody className='tbody'>
                            {filteredArtists.length > 0 ? (
                                filteredArtists.map((artist, index) => (
                                    <tr className='border-b border-gray-700' key={artist.artist_id || index}>
                                        <td className='p-4'>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                        <td className='p-4'>{artist.artist_id}</td>
                                        <td className='p-4'>
                                            {artist.labels.map((label, i) => (
                                                <div key={i} className="text-sm text-gray-300">{label}</div>
                                            ))}
                                        </td>
                                        <td className='p-4'>{artist.artist_names.length}</td>
                                        <td className='p-4'>{artist.song_names.length}</td>
                                        <td className='p-4'>
                                            <button
                                                onClick={() => handleViewDetails(artist)}
                                                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded"
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center p-4">No artists found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PlatformWiseArtistsData;